import { useEffect, useRef, useState } from 'react';

interface FontDropdownProps {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  buttonClassName?: string;
  menuClassName?: string;
}

export function FontDropdown({
  value,
  options,
  onChange,
  buttonClassName = '',
  menuClassName = '',
}: FontDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, []);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`w-full px-3 py-2 rounded-lg text-sm text-left flex items-center justify-between ${buttonClassName}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="truncate">{value}</span>
        <svg
          className={`w-4 h-4 flex-shrink-0 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          className={`absolute left-0 right-0 mt-1 z-[120] max-h-52 overflow-y-auto rounded-lg border shadow-xl ${menuClassName}`}
          role="listbox"
        >
          {options.map((font) => (
            <button
              key={font}
              type="button"
              onClick={() => {
                onChange(font);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                font === value ? 'bg-game-blue text-white' : 'hover:bg-surface-secondary dark:hover:bg-neutral-700'
              }`}
              style={{ fontFamily: font }}
              role="option"
              aria-selected={font === value}
            >
              {font}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
